import {
  MESSAGES_PER_PAGE,
  emptyInfinityModel,
  pageNumberInfinityModel,
  pageNumberInfinityOptions,
} from '../../utils/messaging/infinity-model';
import { MODEL_NAMES } from '../../constants/index';
import {
  getInactiveReasonsFromResponse,
  normalizeInactiveReasons,
} from '../../utils/messaging/conversation';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import MessagingRoutesBaseHandler from './base-handler.js';

export default class MessagingRoutesConversationHandler extends MessagingRoutesBaseHandler {
  @service('spds/toast') toast;
  @service infinity;
  @service intl;
  @service messaging;
  @service messagingLogging;
  @service messagingNavigation;
  @service store;

  @tracked conversation;
  @tracked inactiveReasons = [];
  @tracked messages = emptyInfinityModel();

  constructor(owner, options) {
    super(owner, options);
    this.conversation = options.conversation;
  }

  onVisit = task({ drop: true }, async () => {
    try {
      if (this.conversation.conflicts?.length) {
        this.inactiveReasons = normalizeInactiveReasons(this.conversation.conflicts);
      } else if (this.conversation.inactive) {
        await this.fetchAndValidateUsers.perform(this.conversation);
      }

      await this.getMessages.perform();
    } catch (e) {
      this.messagingLogging.error(e, {
        context: 'conversation onVisit',
        conversationId: this.conversation?.id,
      });
      this.#handleConversationError();
      this.messagingNavigation.toConversationList();
    }
  });

  replaceConversation(newConversation, messages) {
    this.conversation = newConversation;
    this.options = { ...this.options, conversation: newConversation };
    this.resetInactiveReasons();
    this.getMessages.cancelAll();

    if (messages) {
      this.messages = messages;
      return;
    }

    // Clear the previous conversation's messages before reloading so we don't
    // briefly leak them into the new conversation view. `isLoading` covers the
    // visual gap while `getMessages` runs.
    this.messages = emptyInfinityModel();
    this.getMessages.perform();
  }

  getMessages = task({ drop: true }, async () => {
    this.messages = await this.initMessagesInfinityModel.perform();
  });

  loadMoreMessages = task({ drop: true }, async model => {
    try {
      await this.infinity.loadNextPage(model);
    } catch (err) {
      this.messagingLogging.error(err, {
        context: 'conversation loadMoreMessages',
        conversationId: this.conversation?.id,
      });
      this.#handleConversationError();
    }
  });

  fetchAndValidateUsers = task({ drop: true }, async conversation => {
    const response = await this.store
      .adapterFor(MODEL_NAMES.CONVERSATION)
      .fetchAndValidateUsers(conversation);

    if (response.ok) {
      await this.#handleUserFetchSuccess(response, conversation);
    } else {
      await this.#handleUserFetchError(response);
    }
  });

  initMessagesInfinityModel = task({ drop: true }, async () => {
    return await this.infinity.model(
      MODEL_NAMES.MESSAGE,
      {
        conversationId: this.conversation.id,
        include: 'conversation,user',
        filter: {
          'include_system': this.messaging.currentProfile.isClinician,
        },
        ...pageNumberInfinityOptions(MESSAGES_PER_PAGE),
      },
      pageNumberInfinityModel(MESSAGES_PER_PAGE)
    );
  });

  resetInactiveReasons() {
    this.inactiveReasons = [];
  }

  async #handleUserFetchSuccess(response, conversation) {
    this.resetInactiveReasons();
    const json = await response.json();
    await this.#updateUserRecords(json, conversation);
  }

  async #updateUserRecords(json) {
    const unsavedUsers = this.store.peekAll(MODEL_NAMES.USER).filter(user => user.isNew);

    for (const jsonUser of json.data) {
      const normalizedUser = this.store.normalize(MODEL_NAMES.USER, jsonUser);
      const { externalId } = normalizedUser.data.attributes;

      const matchingUnsavedUser = unsavedUsers.find(user => user.externalId === externalId);

      if (matchingUnsavedUser) {
        // Since the user was already created in the backend, we pass the data as an adapter option
        await matchingUnsavedUser.save({ adapterOptions: { data: jsonUser } });
      } else {
        this.store.push(normalizedUser);
      }
    }
  }

  async #handleUserFetchError(response) {
    if (response.status !== 422) {
      const error = new Error(this.intl.t('emc.general_error'));
      error.context = 'fetchAndValidateUsers';
      error.status = response.status;
      error.conversationId = this.conversation?.id;
      throw error;
    }

    this.inactiveReasons = await getInactiveReasonsFromResponse(response);
  }

  #handleConversationError() {
    this.toast.error(this.intl.t('emc.general_error'));
  }
}
